import React, { useRef, useEffect, useMemo } from "react";
import CustomIconButton from "@icarius-common/abmComponents/customIconButton";
import paths from "@icarius-router/paths";
import CommonPage from "@icarius-common/commonPage";
import { getLocalizedString } from "@icarius-localization/strings";
import useIssuePermissions from "./useIssuePermissions";
import CreateEditDialog from "./dialogs/createEditDialog/index";
import useAutomaticProcesses from "@icarius-common/automaticProcesses/useAutomaticProcesses";
import ResultGridDialog from "@icarius-common/automaticProcesses/components/resultGridDialog";
import PickTemplateDialog from "@icarius-pages/employeesProcesses/components/dialogs/pickTemplateDialog";
import { PrintIcon, VisibilityIcon } from "@icarius-icons/index";
import { useDispatch, useSelector } from "react-redux";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import UploadFileDialog from "./dialogs/uploadFileDialog";
import { getUserRole } from "@icarius-app/selectors";
import ViewBalanceDialog from "@icarius-pages/myRequests/components/viewBalanceDialog";
import { useHistory, useLocation } from "react-router-dom";
import SelectProcessDialog from "@icarius-common/automaticProcesses/components/selectProcessDialog";
import PreviewFileRenderer from "@icarius-table/renderersAndFilters/previewFileRenderer";
import useFixedFilters from "@icarius-common/fixedFilters";

const IssuePermissions = () => {
    const dispatch = useDispatch();
    const role = useSelector(getUserRole);

    const location = useLocation();
    const history = useHistory();

    const ownGridRef = useRef();
    const hasSetFilters = useRef(false);

    const title = `${getLocalizedString("issuePermissions")}${
        Boolean(location?.state?.name) ? ` - ${location.state.name}` : ""
    }`;

    const { data, isLoading, state, handlers, hasSignedDocument } = useIssuePermissions();

    const {
        isExecution,
        automaticProcessesIsLoading,
        automaticProcessesIsEnabled,
        processList,
        resultData,
        resultDialogIsOpen,
        pickTemplateDialogIsOpen,
        processSelectionDialogIsOpen,
        executeProcess,
        handleExecute,
        viewResult,
        handleViewResult,
        handleCloseProcessDialogSelection,
        handleCloseResultDialog,
        handleOpenPickTemplateDialog,
        handleClosePickTemplateDialog,
        handleGenerateDocumentsGivenTemplate,
    } = useAutomaticProcesses(paths.calculateProcessIssuesButton, role === "M");

    const { hasFixedFilters, toggleFixedFilters } = useFixedFilters(title);

    useEffect(() => {
        if (data && ownGridRef.current && location?.state && !Boolean(hasSetFilters.current)) {
            ownGridRef.current.api.setFilterModel({
                "Fecha desde": { type: "iguales", filter: location?.state.date },
            });

            hasSetFilters.current = true;
        }
    }, [data, location]);

    const handleValidateAndExecute = (gridRef) => {
        let selectedRows = gridRef.api.getSelectedRows();
        if (selectedRows.length < 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectAtLeastOneRow"), severity: "warning" }));
            return;
        }

        const employeeCodes = selectedRows.map((item) => item["Código de empleado"]);
        handleExecute(employeeCodes);
    };

    const handleValidateAndViewResult = (gridRef) => {
        let selectedRows = gridRef.api.getSelectedRows();
        if (selectedRows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOnlyOneRow"), severity: "warning" }));
            return;
        }

        handleViewResult(selectedRows[0], selectedRows[0]["Código de empleado"]);
    };

    const executeItem = (gridRef) =>
        automaticProcessesIsEnabled && (
            <CustomIconButton
                title={
                    processList.length === 1
                        ? `${getLocalizedString("calculateProcess")}: ${processList[0].value}`
                        : getLocalizedString("calculateProcess")
                }
                onClick={() => handleValidateAndExecute(gridRef)}
                type={"execute"}
            />
        );

    const viewResultItem = (gridRef) =>
        automaticProcessesIsEnabled && (
            <CustomIconButton
                title={getLocalizedString("viewResult")}
                onClick={() => handleValidateAndViewResult(gridRef)}
            >
                <VisibilityIcon />
            </CustomIconButton>
        );

    const generateItem = (gridRef) => (
        <CustomIconButton
            title={
                hasSignedDocument
                    ? getLocalizedString("generateVoucherAndSendToCollaboratorForDigitalSignature")
                    : getLocalizedString("generateReceipt")
            }
            onClick={() => handlers.handleGenerateFile(gridRef)}
        >
            <PrintIcon />
        </CustomIconButton>
    );

    const createButton = () => (
        <CustomIconButton title={getLocalizedString("create")} onClick={handlers.handleOpenCreateDialog} type={"add"} />
    );

    const modifyButton = (gridRef) => (
        <CustomIconButton
            title={getLocalizedString("edit")}
            onClick={() => handlers.handleOpenEditDialog(gridRef)}
            type={"edit"}
        />
    );

    const deleteButton = (gridRef) => (
        <CustomIconButton
            title={getLocalizedString("delete")}
            onClick={() => handlers.handleOpenDeleteDialog(gridRef)}
            type={"delete"}
        />
    );

    const exportButton = () => (
        <CustomIconButton
            title={getLocalizedString("exportTemplateForImport")}
            onClick={handlers.handleDownloadStructure}
            type={"download"}
        />
    );

    const importButton = () => (
        <CustomIconButton
            title={getLocalizedString("importPermissions")}
            onClick={handlers.handleOpenUploadFileDialogIsOpen}
            type={"upload"}
        />
    );

    const goBackButton = () => (
        <CustomIconButton
            title={getLocalizedString("goBack")}
            type={"goBack"}
            onClick={() =>
                history.push(
                    Boolean(location?.state?.from) ? location?.state.from : paths.issueManagementScreenSelector
                )
            }
        />
    );

    const filtersItem = (gridRef) => (
        <CustomIconButton
            title={hasFixedFilters ? getLocalizedString("removeFixedFilters") : getLocalizedString("setFixedFilters")}
            onClick={() => toggleFixedFilters(gridRef, false)}
            type={"fixedFilter"}
            isSelected={hasFixedFilters}
        />
    );

    const filteredData = useMemo(() => {
        if (!data) return [];
        if (!Boolean(location?.state?.code)) return data;
        return data.filter((item) => item["Código de empleado"] === location.state?.code);
    }, [data, location]);

    return (
        <CommonPage
            isLoading={isLoading || automaticProcessesIsLoading}
            title={title}
            gridTitle={title}
            columnDefPage={paths.issuePermissions}
            rowData={filteredData}
            menuItems={[
                goBackButton,
                createButton,
                modifyButton,
                deleteButton,
                exportButton,
                importButton,
                generateItem,
                executeItem,
                viewResultItem,
                filtersItem,
            ]}
            hasExpand
            hasHelp
            ownGridRef={ownGridRef}
            frameworkComponents={{ PreviewFileRenderer }}
            hasFixedFilters
        >
            {(state.createDialogIsOpen || state.editDialogIsOpen) && (
                <CreateEditDialog
                    open={state.createDialogIsOpen || state.editDialogIsOpen}
                    isLoading={isLoading}
                    data={state.selectedDocument}
                    handleClose={handlers.handleCloseCreateEditDialog}
                    handleOpenBalanceDialog={handlers.handleOpenBalanceDialog}
                    presetEmployee={location?.state?.code}
                />
            )}
            {processSelectionDialogIsOpen && (
                <SelectProcessDialog
                    open={processSelectionDialogIsOpen}
                    title={
                        isExecution
                            ? getLocalizedString("selectProcessToRun")
                            : getLocalizedString("selectProcessToQuery")
                    }
                    processList={processList}
                    handleClose={handleCloseProcessDialogSelection}
                    handleExecute={isExecution ? executeProcess : viewResult}
                />
            )}
            {resultDialogIsOpen && !(isLoading || automaticProcessesIsLoading) && (
                <ResultGridDialog
                    open={resultDialogIsOpen}
                    templates={resultData.templates}
                    process={resultData.process}
                    employeeName={resultData.employee["Apellido y nombres"]}
                    handleClose={handleCloseResultDialog}
                    handleOpenPickTemplateDialog={handleOpenPickTemplateDialog}
                    handleGenerateDocumentsGivenTemplate={handleGenerateDocumentsGivenTemplate}
                />
            )}
            {resultData?.templates?.length > 0 &&
                pickTemplateDialogIsOpen &&
                !(isLoading || automaticProcessesIsLoading) && (
                    <PickTemplateDialog
                        open={pickTemplateDialogIsOpen}
                        templates={resultData.templates}
                        handleConfirm={handleGenerateDocumentsGivenTemplate}
                        handleClose={handleClosePickTemplateDialog}
                    />
                )}
            {state.uploadFileDialogIsOpen && (
                <UploadFileDialog
                    open={state.uploadFileDialogIsOpen}
                    isLoading={isLoading}
                    handleClose={handlers.handleCloseUploadFileDialogIsOpen}
                />
            )}
            {state.balanceDialogIsOpen && (
                <ViewBalanceDialog
                    open={state.balanceDialogIsOpen}
                    data={state.balanceData}
                    handleClose={handlers.handleCloseBalanceDialog}
                />
            )}
        </CommonPage>
    );
};

export default IssuePermissions;
